import { useState, useTransition } from 'react';
import { callApi, ShopifyAppApiError } from '@/lib/api';
import { BrandCard } from './buds-header';

interface ShopifyCategorySyncError {
  externalId: string;
  message: string;
}

interface ShopifyCategorySyncSummary {
  pushed: number;
  errors: ShopifyCategorySyncError[];
  durationMs: number;
}

interface Props {
  onReconnectRequired: (message?: string) => void;
}

function formatErrorMessage(code: string): string {
  switch (code) {
    case 'NOT_CONNECTED':
      return 'No store is connected. Connect a Buds API key first.';
    case 'NOT_FOUND':
      return 'No Shopify connection on file. Try reconnecting the app.';
    case 'CONFLICT':
      return "Sync can't run right now. Make sure the app is still connected to Shopify; reconnect if you recently revoked the token.";
    case 'INVALID_KEY':
      return 'Buds rejected the saved API key. Reconnect with a fresh key.';
    case 'BUDS_UNREACHABLE':
      return 'Could not reach Buds. Try again in a moment.';
    default:
      return `Sync failed (${code}).`;
  }
}

export function SyncCategoriesCard({ onReconnectRequired }: Props) {
  const [pending, startTransition] = useTransition();
  const [summary, setSummary] = useState<ShopifyCategorySyncSummary | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [showAllErrors, setShowAllErrors] = useState(false);

  async function run() {
    setError(null);
    setSummary(null);
    try {
      const body = await callApi<ShopifyCategorySyncSummary>(
        '/v1/shopify-app/sync-categories',
        { method: 'POST', body: {} },
      );
      setSummary(body);
    } catch (err) {
      const e = err as ShopifyAppApiError;
      if (e.code === 'NOT_CONNECTED') {
        onReconnectRequired(e.message);
        return;
      }
      setError(formatErrorMessage(e.code));
    }
  }

  const disabled = pending;

  return (
    <BrandCard title="Sync categories">
      <p className="text-sm text-buds-dark/70">
        Push every Buds reward category to your Shopify shop as a{' '}
        <code className="font-mono">$app:category</code> metaobject. This is
        only needed for choosing per-product overrides in Shopify; the default
        category setting works without it.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => startTransition(() => run())}
          disabled={disabled}
          className="inline-flex flex-1 items-center justify-center rounded-full bg-buds-dark px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-buds-dark/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-buds-green focus-visible:ring-offset-2 focus-visible:ring-offset-buds-cream disabled:cursor-not-allowed disabled:opacity-60"
        >
          {pending ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-400/60 bg-red-50 px-3 py-2 text-xs text-red-900">
          {error}
        </div>
      )}

      {summary && (
        <div className="space-y-2 rounded-md border border-buds-dark/10 bg-buds-surface/50 px-3 py-3 text-xs text-buds-dark">
          <div className="grid grid-cols-1 gap-2">
            <Stat label="Pushed" value={summary.pushed} />
          </div>
          <p className="text-buds-dark/60">
            Completed in {summary.durationMs} ms.
          </p>
          {summary.errors.length > 0 && (
            <ErrorList
              errors={summary.errors}
              expanded={showAllErrors}
              onToggle={() => setShowAllErrors((v) => !v)}
            />
          )}
        </div>
      )}
    </BrandCard>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-white px-3 py-2 shadow-sm">
      <div className="text-[10px] font-medium uppercase tracking-wide text-buds-dark/60">
        {label}
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function ErrorList({
  errors,
  expanded,
  onToggle,
}: {
  errors: ShopifyCategorySyncError[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const visible = expanded ? errors : errors.slice(0, 3);
  return (
    <div className="rounded-md border border-red-300/60 bg-red-50 px-3 py-2 text-red-900">
      <div className="flex items-center justify-between">
        <span className="font-medium">{errors.length} item(s) failed</span>
        {errors.length > 3 && (
          <button
            type="button"
            onClick={onToggle}
            className="text-[11px] underline"
          >
            {expanded ? 'Show fewer' : `Show all (${errors.length})`}
          </button>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {visible.map((err) => (
          <li key={err.externalId} className="break-words">
            <code className="font-mono text-[11px]">{err.externalId}</code>{' '}
            &mdash; {err.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
